import { truncateStringWithLength } from '../../../utils';
import { attemptFormatDate } from '../../../utils/formatting';
import { getTextWidth } from '../utils';
import { X_AXIS_ROTATED_HEIGHT } from '../constants/utils';
import { theme } from '../../../styles/theme';

const FONT_SIZE = 10;
const ROTATE_TEXT_ANGLE = -45;
interface IPayload {
  value: string;
}

interface ICustomXAxisTickProps {
  x: number;
  y: number;
  payload: IPayload;
  barSize?: number;
  // only need to rotate the label in expanded view
  // will remove in the future once we introduce the better expanded view.
  isAllowLabelRotated?: boolean;
  width: number;
  visibleTicksCount: number;
  isTruncateLabel?: boolean;
}

// given the container width and the font size, truncate the text to fit the container width
// append three dots if the text is truncated
// if the text is rotated, need to calculate the max width of the text
const truncateTextByContainerWidth = (
  text: string,
  labelContainerWidth: number,
  fontSize: number,
  isLabelRotated: boolean,
) => {
  // text width is the px of the full text
  const textWidth = getTextWidth(text, `${fontSize}px`);

  // if the text is rotated, need to calculate the max width of the text after rotation.
  // since we rotate -45 degree and the space is 80px on x-axis,
  // use (80 / sin(90 - 45)) to calculate the max length of the rotated text
  const rotatedContainerWidth = isLabelRotated
    ? X_AXIS_ROTATED_HEIGHT /
      Math.sin(Math.abs(ROTATE_TEXT_ANGLE) * (Math.PI / 180))
    : labelContainerWidth;
  // if full label text is longer than the ROTATED bar container width, truncate it.
  if (textWidth > rotatedContainerWidth) {
    // 2 is the ratio of the font size to the text width.
    const updatedTextLength = Math.floor(rotatedContainerWidth / fontSize) * 2;

    return truncateStringWithLength(text, updatedTextLength);
  }
  return text;
};

// Orginally, the end of text is align with the right-border of the bar.
// Now, if the text is short, try to align it's center to the tick position.
const getOffsetAlignMedianToTick = (truncatedText: string, barSize: number) => {
  const slopeLength = getTextWidth(truncatedText, `${FONT_SIZE}px`);

  // if the text is longer than the bar,
  // return 0 and align the text to the right-border of the bar.
  //
  // Add -2 offsets for edge case for the text is closing to the bar size,
  // use align to the right-border of the bar
  if (slopeLength > barSize - 2) {
    return 0;
  }
  // labelHorizontalLength is the px length of the label from slope reflect to horizontal line
  const labelHorizontalLength =
    slopeLength * Math.cos(Math.abs(ROTATE_TEXT_ANGLE) * (Math.PI / 180));

  // offset calculation is diff for cases:
  // 1. label is shorter or equal to the median point
  // 2. label is longer than the median point
  if (labelHorizontalLength <= barSize / 2) {
    return -(barSize / 2 - labelHorizontalLength);
  } else {
    return barSize / 2 - labelHorizontalLength;
  }
};

export default ({
  x,
  y,
  payload: { value },
  barSize,
  isAllowLabelRotated,
  width,
  visibleTicksCount,
  isTruncateLabel,
}: ICustomXAxisTickProps) => {
  const formatedValue = attemptFormatDate(value);

  const isLabelRotated = !!(isAllowLabelRotated && barSize);
  // get avg width of each label
  const labelContainerWidth = width / visibleTicksCount;
  const truncatedText = truncateTextByContainerWidth(
    formatedValue,
    labelContainerWidth,
    FONT_SIZE,
    isLabelRotated,
  );

  if (isLabelRotated) {
    // this prefix is increased to 12 when the bar width is larger than 36px
    // the prefix will help the label align with the x-axis tick
    // ratio 5 is the best positioning with the bar width < 48.
    const xAxisPrefixOnRotate = barSize > 48 ? 12 : Math.floor(barSize / 6);
    const tickOffset = getOffsetAlignMedianToTick(truncatedText, barSize);
    return (
      <g
        transform={`translate(${xAxisPrefixOnRotate + x + tickOffset},${y - 4})`}
      >
        <g transform={`rotate(${ROTATE_TEXT_ANGLE})`}>
          <text
            dy={16}
            textAnchor="end"
            fill={theme.colors.secondary600}
            fontSize={`${FONT_SIZE}px`}
            fontWeight={500}
          >
            {truncatedText}
          </text>
        </g>
      </g>
    );
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={16}
        textAnchor="middle"
        fill={theme.colors.secondary600}
        fontSize={FONT_SIZE}
        fontWeight={400}
      >
        {isTruncateLabel ? truncatedText : formatedValue}
      </text>
    </g>
  );
};
